import React from 'react'
import {
  ButtonVariant,
  MaxWidthConstraint,
  OneByFourGrid,
  OvalButton,
  PhotoHero,
  Spacer,
  WorkArea,
} from '@revelrygroup/components'
import { GetServerSideProps } from 'next'
import Head from 'next/head'
import Image from 'next/image'

import { API } from '../revelry_api'
import { FuneralHome } from '../types'
import { FuneralHomeFooter } from '../components/FuneralHomeFooter'
import { FuneralHomeModel } from '../components/models/FuneralHomeModel'
import Layout from '../components/Layout'
import { routes } from '../routes'
import * as Showcase from '../components/Showcase'
import { Testimonials } from '../components/Testimonials'

interface Props {
  funeralHome: FuneralHome | null
}

const HowWeCelebrate: React.FC<Props> = ({ funeralHome }) => {
  return (
    <>
      <Head>
        <title>
          How We Celebrate - {funeralHome.name} - {FuneralHomeModel.headTagLocation(funeralHome)}
        </title>

        <meta
          name="description"
          content={`${funeralHome.name} specializes in personalized funeral services and celebrations, including memorial services, burials, embalmings, and other personalized ways to celebrate the life of your loved one.`}
          key="description"
        />
      </Head>

      <Layout>
        <PhotoHero>How We Celebrate</PhotoHero>

        <div className="height-100">
          <main>
            <WorkArea className="blue-gradient-background">
              <MaxWidthConstraint>
                <Image
                  alt="Family celebrating"
                  height={430}
                  layout="responsive"
                  src="/photo-family-beach@2x.jpg"
                  width={1024}
                />

                <Spacer vertical="xs">
                  <h2 className="h2 centered-text">Every Life Deserves to be Celebrated</h2>
                </Spacer>

                <Spacer verticalTop="sm">
                  <h5 className="h5 centered-text">
                    When you lose someone you cherish, your heart is breaking, and you may feel
                    lost. A way to begin healing is to celebrate your loved one, by sharing how much
                    they mean to you, family and friends.
                  </h5>
                </Spacer>

                <Spacer verticalTop="sm">
                  <h5 className="h5 centered-text">
                    When you gather, tell stories, share memories, say their name, and feel the
                    love, it encourages a focus on the positive, more uplifting aspects of life.
                  </h5>
                </Spacer>
              </MaxWidthConstraint>
            </WorkArea>

            <Showcase.Wrapper backgroundImage="/radian.png">
              <Showcase.Primary>
                <Showcase.Image alt="People with sparklers" src="/photo-sparklers@2x.jpg" />
              </Showcase.Primary>

              <Showcase.Secondary>
                <Showcase.Title className="centered-text-mobile">
                  Why It&apos;s Important to Celebrate Your Loved One
                </Showcase.Title>

                <Showcase.Text>
                  It&apos;s human nature to want to remember those we&apos;ve loved and lost. To
                  mend, to heal, to live, and never forget.
                </Showcase.Text>

                <Showcase.Text>
                  Whether your loved one chooses a casket, urn, or both, it&apos;s comforting for
                  your family and friends to be together. Holding a celebration is not only a way to
                  say goodbye, but it also helps you move from grieving their loss to treasuring
                  their memory.
                </Showcase.Text>
              </Showcase.Secondary>
            </Showcase.Wrapper>

            <WorkArea style={{ backgroundColor: 'white' }}>
              <MaxWidthConstraint className="centered-text">
                <Spacer verticalBottom="xs">
                  <h2 className="h2">Personalized Celebrations</h2>
                </Spacer>

                <Spacer verticalBottom="xs">
                  <p className="body">
                    We always appreciate being able to work with families in planning a unique
                    service that pays tribute to and celebrates the life and spirit of a loved one.
                    There are many ways to plan a meaningful &amp; memorable celebration that honors
                    their legacy, and we are open to any ideas your family has. We also have ideas
                    we can share.
                  </p>
                </Spacer>

                <Spacer verticalBottom="xs">
                  <p className="body">
                    Just as each person is unique, each celebration is special. The sky&apos;s the
                    limit! You can create a tribute movie or slide show, display photos or
                    scrapbooks on a memory table, choose a theme, personalized music, special
                    readings, keepsakes or special food and drinks.
                  </p>
                </Spacer>

                <Spacer verticalTop="sm">
                  <OvalButton as="Link" href={routes.contact} variant={ButtonVariant.DEFAULT_DARK}>
                    Contact Us
                  </OvalButton>
                </Spacer>

                <Spacer verticalTop="md">
                  <OneByFourGrid>
                    <div>
                      <Image
                        alt="Family photos"
                        height={277}
                        layout="responsive"
                        src="/photo-photos@2x.jpg"
                        width={277}
                      />
                    </div>

                    <div>
                      <Image
                        alt="Wine glasses"
                        height={277}
                        layout="responsive"
                        src="/photo-wine-glasses@2x.jpg"
                        width={277}
                      />
                    </div>

                    <div>
                      <Image
                        alt="Invitations"
                        height={277}
                        layout="responsive"
                        src="/photo-invitations@2x.jpg"
                        width={277}
                      />
                    </div>

                    <div>
                      <Image
                        alt="Pastries"
                        height={277}
                        layout="responsive"
                        src="/photo-pastries@2x.jpg"
                        width={277}
                      />
                    </div>
                  </OneByFourGrid>
                </Spacer>
              </MaxWidthConstraint>
            </WorkArea>

            <Testimonials />
          </main>

          <FuneralHomeFooter />
        </div>
      </Layout>
    </>
  )
}

export const getServerSideProps: GetServerSideProps<Props> = async () => {
  try {
    const funeralHome = await API.funeralHomes.load()

    return { props: { funeralHome } }
  } catch (e) {
    return { props: { funeralHome: null } }
  }
}

export default HowWeCelebrate
